import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTruck, FaGlobe, FaClipboardCheck, FaUserShield, FaArrowRight, FaShip, FaStore, FaExchangeAlt, FaCheckCircle } from 'react-icons/fa';
import { getTransitServices } from '../services/api';
import { Link } from 'react-router-dom';
import Loader from '../components/Loader';
import Reveal from '../components/Reveal';
import { gridContainer, gridItem, cardHover, heroContainer, viewportOnce } from '../components/motionPresets';
import { useT } from '../i18n/LanguageContext';
import './Transit.css';

const T = {
    en: {
        heroTitle: 'Transit Solutions',
        heroSubtitle: 'Comprehensive services to simplify the transit of your goods',
        introTitle: 'Simplifying Goods Transit',
        introSubtitle:
            "Feeling challenged by the complexities of goods transit? We provide comprehensive services designed to simplify the process from start to finish.",
        highlightRegulationsTitle: 'Expert Insight into Transit Regulations',
        highlightRegulationsText:
            'Balancing costs with high-quality service is essential for transit managers. Our actionable insights help you discover cost-saving opportunities and streamline operations without compromising quality.',
        highlightDeliveryTitle: 'Customer-Centric Delivery Solutions',
        highlightDeliveryText:
            'We prioritize flexibility, ensuring goods are delivered when and how your customers want them, fostering loyalty and satisfaction at every touchpoint.',
        servicesTitle: 'Our Transit Services',
        svcImportTitle: 'Import & transportation of goods from China by sea and air',
        svcRetailTitle: 'In-store retail purchases',
        svcFullRangeTitle: 'Full range of transit services',
        svcFullRangeItem1: 'Purchase of products in China without transit',
        svcFullRangeItem2: 'Purchase of products in China + full transit management',
        svcFullRangeItem3: 'Transit management only (for goods already purchased)',
        loadingServices: 'Loading services',
        priceRange: 'Price Range:',
        requestService: 'Request Service',
        howItWorksTitle: 'How It Works',
        step1Title: 'Consultation',
        step1Text: "Tell us about your transit needs and we'll create a customized plan.",
        step2Title: 'Planning',
        step2Text: 'We handle all logistics, customs clearance, and route optimization.',
        step3Title: 'Transit',
        step3Text: 'Your goods are transported safely with real-time tracking.',
        step4Title: 'Delivery',
        step4Text: 'Goods arrive on time at the designated location.',
        ctaTitle: 'Need Reliable Transit Solutions?',
        ctaText: 'Let us handle the complexities while you focus on growing your business.',
        getQuote: 'Get a Quote',
    },
    fr: {
        heroTitle: 'Solutions de transit',
        heroSubtitle: 'Des services complets pour simplifier le transit de vos marchandises',
        introTitle: 'Simplifier le transit des marchandises',
        introSubtitle:
            "Les complexités du transit des marchandises vous semblent difficiles ? Nous proposons des services complets conçus pour simplifier le processus du début à la fin.",
        highlightRegulationsTitle: 'Expertise des réglementations de transit',
        highlightRegulationsText:
            "Concilier les coûts et un service de haute qualité est essentiel pour les responsables de transit. Nos analyses concrètes vous aident à identifier des opportunités d'économies et à optimiser vos opérations sans compromettre la qualité.",
        highlightDeliveryTitle: 'Des solutions de livraison axées sur le client',
        highlightDeliveryText:
            'Nous privilégions la flexibilité, en veillant à ce que les marchandises soient livrées quand et comme vos clients le souhaitent, favorisant ainsi la fidélité et la satisfaction à chaque étape.',
        servicesTitle: 'Nos services de transit',
        svcImportTitle: 'Importation et transport de marchandises depuis la Chine par voie maritime et aérienne',
        svcRetailTitle: 'Achats au détail en magasin',
        svcFullRangeTitle: 'Gamme complète de services de transit',
        svcFullRangeItem1: 'Achat de produits en Chine sans transit',
        svcFullRangeItem2: 'Achat de produits en Chine + gestion complète du transit',
        svcFullRangeItem3: 'Gestion du transit uniquement (pour les marchandises déjà achetées)',
        loadingServices: 'Chargement des services',
        priceRange: 'Fourchette de prix :',
        requestService: 'Demander le service',
        howItWorksTitle: 'Comment ça marche',
        step1Title: 'Consultation',
        step1Text: 'Parlez-nous de vos besoins en transit et nous élaborerons un plan personnalisé.',
        step2Title: 'Planification',
        step2Text: "Nous gérons toute la logistique, le dédouanement et l'optimisation des itinéraires.",
        step3Title: 'Transit',
        step3Text: 'Vos marchandises sont transportées en toute sécurité avec un suivi en temps réel.',
        step4Title: 'Livraison',
        step4Text: "Les marchandises arrivent à l'heure au lieu indiqué.",
        ctaTitle: 'Besoin de solutions de transit fiables ?',
        ctaText: 'Laissez-nous gérer les complexités pendant que vous vous concentrez sur le développement de votre activité.',
        getQuote: 'Obtenir un devis',
    },
};

function Transit() {
    const t = useT(T);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);

    // Service offerings shown under "Our Transit Services".
    const SERVICES = [
        { icon: <FaShip />, title: t.svcImportTitle },
        { icon: <FaStore />, title: t.svcRetailTitle },
        {
            icon: <FaExchangeAlt />,
            title: t.svcFullRangeTitle,
            items: [t.svcFullRangeItem1, t.svcFullRangeItem2, t.svcFullRangeItem3],
        },
    ];

    useEffect(() => {
        getTransitServices()
            .then(res => setServices(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="transit-page">
            <motion.section className="page-hero" initial="hidden" animate="show" variants={heroContainer}>
                <motion.h1 variants={gridItem}>{t.heroTitle}</motion.h1>
                <motion.p variants={gridItem}>{t.heroSubtitle}</motion.p>
            </motion.section>

            <section className="transit-intro">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.introTitle}</h2></Reveal>
                    <Reveal delay={0.08}><p className="section-subtitle">
                        {t.introSubtitle}
                    </p></Reveal>
                    <motion.div className="transit-highlights" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        <motion.div className="transit-highlight" variants={gridItem} whileHover={cardHover}>
                            <FaClipboardCheck className="th-icon" />
                            <h3>{t.highlightRegulationsTitle}</h3>
                            <p>
                                {t.highlightRegulationsText}
                            </p>
                        </motion.div>
                        <motion.div className="transit-highlight" variants={gridItem} whileHover={cardHover}>
                            <FaUserShield className="th-icon" />
                            <h3>{t.highlightDeliveryTitle}</h3>
                            <p>
                                {t.highlightDeliveryText}
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <section className="transit-services-section">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.servicesTitle}</h2></Reveal>
                    <motion.div className="svc-grid" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        {SERVICES.map((svc, i) => (
                            <motion.div
                                key={i}
                                className={`svc-card ${svc.items ? 'svc-card-wide' : ''}`}
                                variants={gridItem}
                                whileHover={cardHover}
                            >
                                <div className="svc-icon">{svc.icon}</div>
                                <h3>{svc.title}</h3>
                                {svc.items && (
                                    <ul className="svc-sublist">
                                        {svc.items.map((it, j) => (
                                            <li key={j}><FaCheckCircle /> {it}</li>
                                        ))}
                                    </ul>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                    {loading && <Loader label={t.loadingServices} />}
                    {!loading && (
                    <motion.div className="transit-grid" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        {services.map(service => (
                            <motion.div key={service.id} className="transit-card" variants={gridItem} whileHover={cardHover}>
                                <div className="transit-card-icon">
                                    {service.service_type === 'International' ? <FaGlobe /> : <FaTruck />}
                                </div>
                                <div className="transit-badge">{service.service_type}</div>
                                <h3>{service.name}</h3>
                                <p>{service.description}</p>
                                <div className="transit-price">
                                    <span>{t.priceRange}</span>
                                    <strong>{service.price_range}</strong>
                                </div>
                                <Link to="/contact" className="btn btn-primary transit-btn">
                                    {t.requestService} <FaArrowRight />
                                </Link>
                            </motion.div>
                        ))}
                    </motion.div>
                    )}
                </div>
            </section>

            <section className="transit-process">
                <div className="section-container">
                    <Reveal><h2 className="section-title">{t.howItWorksTitle}</h2></Reveal>
                    <motion.div className="process-steps" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        <motion.div className="process-step" variants={gridItem} whileHover={cardHover}>
                            <div className="step-number">1</div>
                            <h3>{t.step1Title}</h3>
                            <p>{t.step1Text}</p>
                        </motion.div>
                        <motion.div className="process-step" variants={gridItem} whileHover={cardHover}>
                            <div className="step-number">2</div>
                            <h3>{t.step2Title}</h3>
                            <p>{t.step2Text}</p>
                        </motion.div>
                        <motion.div className="process-step" variants={gridItem} whileHover={cardHover}>
                            <div className="step-number">3</div>
                            <h3>{t.step3Title}</h3>
                            <p>{t.step3Text}</p>
                        </motion.div>
                        <motion.div className="process-step" variants={gridItem} whileHover={cardHover}>
                            <div className="step-number">4</div>
                            <h3>{t.step4Title}</h3>
                            <p>{t.step4Text}</p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <section className="cta-section">
                <Reveal className="cta-content" y={36}>
                    <h2>{t.ctaTitle}</h2>
                    <p>{t.ctaText}</p>
                    <Link to="/contact" className="btn btn-primary">
                        {t.getQuote} <FaArrowRight />
                    </Link>
                </Reveal>
            </section>
        </div>
    );
}

export default Transit;
